/**
 * Workflow redesign: the catalog of every trigger and action (design "Workflows UI redesign", the
 * T / A lists in Workflow Frame.dc.html; Workflow overhaul P2, 2026-10-10).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE and without a default export (the node-tests execute it verbatim:
 * cypress/node-tests/workflow-block-catalog.test.js).
 *
 * Three names per block, and they are NOT interchangeable:
 *   type       the builder's node type (lowercase, no separators): 'appointmentcompleted', 'assigntask'.
 *   blockType  what the save code writes: the workflow's `TriggerOn` and the `blockelemtype` of every
 *              block in `FlowyText`: 'APPOINTMENT_COMPLETED', 'ASSIGN_TASK'. It is the output of the
 *              classic builder's mapTypeToBlockType (WorkflowBuilder.vue); the node test reads that
 *              method and fails if the two ever disagree.
 *   label      the sentence-case name the redesign shows. The classic builder keeps its own title-case
 *              labels, which are NOT edited.
 *
 * `soon` marks a block the classic builder still has switched off (`enabled: false`); the redesign
 * lists it greyed out. `gated` marks a trigger that needs a site setting before it can fire (Mango Voice
 * needs MANGO_VOICE_SECURITY_TOKEN). `description` is the design's one-liner; the design gives none to a
 * block that is not available yet, so those stay ''.
 *
 * `icon` is a key of WF_ICONS (src/pages/Workflow2/redesign/wfIcons.js), pinned by the node test.
 *
 * An unknown value is never an error: a label falls back to the raw value and an icon to a generic one,
 * so a workflow saved by an older (or newer) builder still draws.
 */

export const DEFAULT_TRIGGER_ICON = 'zap';
export const DEFAULT_ACTION_ICON = 'workflow';

export const TRIGGERS = [
  { type: 'appointmentscheduled', blockType: 'APPOINTMENT_SCHEDULED', label: 'Appointment scheduled', description: 'When a new appointment is scheduled', icon: 'calPlus', soon: false, gated: false },
  { type: 'appointmentbroken', blockType: 'APPOINTMENT_BROKEN', label: 'Appointment broken', description: 'When an appointment is broken or cancelled', icon: 'calX', soon: false, gated: false },
  { type: 'appointmentcompleted', blockType: 'APPOINTMENT_COMPLETED', label: 'Appointment completed', description: 'When an appointment is marked complete', icon: 'calCheck', soon: false, gated: false },
  { type: 'procaddedquickadd', blockType: 'PROCEDURE_ADDED_VIA_PROCS_QUICK_ADD', label: 'Procedure added via Procs Quick Add', description: 'When a procedure is added from Account › Procedures › Quick Add in Open Dental', icon: 'filePlus', soon: false, gated: false },
  { type: 'mangovoice', blockType: 'MANGO_VOICE', label: 'Mango Voice event', description: 'On a Mango Voice phone event, like a missed call', icon: 'phone', soon: false, gated: true },
  { type: 'newpatient', blockType: 'NEW_PATIENT', label: 'New patient', description: '', icon: 'userPlus', soon: true, gated: false },
  { type: 'preauthreceived', blockType: 'PRE_AUTH_RECEIVED', label: 'Pre-auth marked as received', description: '', icon: 'fileCheck', soon: true, gated: false },
  { type: 'xfollowups', blockType: 'X_FOLLOWUPS', label: 'Teamio follow-ups completed', description: '', icon: 'squareCheck', soon: true, gated: false },
  { type: 'proceduremarkedreferral', blockType: 'PROCEDURE_MARKED_REFERRAL', label: 'Procedure marked as referral', description: '', icon: 'userCheck', soon: true, gated: false },
  { type: 'websiteform', blockType: 'WEBSITE_FORM', label: 'New website form submission', description: '', icon: 'fileText', soon: true, gated: false },
  { type: 'facebooklead', blockType: 'FACEBOOK_LEAD', label: 'Facebook lead generated', description: '', icon: 'target', soon: true, gated: false },
  { type: 'googlelead', blockType: 'GOOGLE_LEAD', label: 'Google lead generated', description: '', icon: 'globe', soon: true, gated: false },
];

export const ACTIONS = [
  { type: 'sms', blockType: 'SMS', label: 'Send text message', description: 'Text the patient', icon: 'msg', soon: false },
  { type: 'email', blockType: 'EMAIL', label: 'Send email', description: 'Email the patient', icon: 'mail', soon: false },
  { type: 'assigntask', blockType: 'ASSIGN_TASK', label: 'Assign Teamio task', description: 'Give someone on your team a task', icon: 'clipCheck', soon: false },
  { type: 'kanban', blockType: 'KANBAN', label: 'Kanban', description: 'Add, update, or move a card on a board', icon: 'kanban', soon: false },
  { type: 'popup', blockType: 'POPUP', label: 'Create an Open Dental popup', description: '', icon: 'appWindow', soon: true },
  { type: 'mailletter', blockType: 'MAIL_LETTER', label: 'Mail letter', description: '', icon: 'mailOpen', soon: true },
  { type: 'wait', blockType: 'WAIT', label: 'Wait', description: '', icon: 'hourglass', soon: true },
  { type: 'slack', blockType: 'SLACK', label: 'Send Slack message', description: '', icon: 'hash', soon: true },
  { type: 'prospectivelead', blockType: 'PROSPECTIVE_LEAD', label: 'Create a follow-up for a prospective lead', description: '', icon: 'eye', soon: true },
  { type: 'sendform', blockType: 'SEND_FORM', label: 'Send a form', description: '', icon: 'fileText', soon: true },
];

/** A builder type as stored: trimmed, lowercase. null / undefined / non-strings become ''. */
function cleanType(value) {
  return value === null || value === undefined ? '' : String(value).trim().toLowerCase();
}

/** A saved block type as stored: trimmed, UPPER_SNAKE ('Assign Task' and 'assign-task' read the same). */
function cleanBlockType(value) {
  return value === null || value === undefined ? '' : String(value).trim().toUpperCase().replace(/[\s-]+/g, '_');
}

function findBy(list, key, value) {
  if (value === '') return null;
  for (let i = 0; i < list.length; i += 1) {
    if (list[i][key] === value) return list[i];
  }
  return null;
}

/** The trigger with this builder type, or null. */
export function triggerByType(type) {
  return findBy(TRIGGERS, 'type', cleanType(type));
}

/** The action with this builder type, or null. */
export function actionByType(type) {
  return findBy(ACTIONS, 'type', cleanType(type));
}

/** The trigger with this saved block type (a workflow's `TriggerOn`), or null. */
export function triggerByBlockType(blockType) {
  return findBy(TRIGGERS, 'blockType', cleanBlockType(blockType));
}

/** The action with this saved block type (a FlowyText `blockelemtype`), or null. */
export function actionByBlockType(blockType) {
  return findBy(ACTIONS, 'blockType', cleanBlockType(blockType));
}

/** The saved block type for a builder type, or null when the catalog does not know the type. */
export function blockTypeOf(type) {
  const entry = triggerByType(type) || actionByType(type);
  return entry ? entry.blockType : null;
}

/**
 * Whatever a saved workflow holds for a trigger: the block type ('APPOINTMENT_COMPLETED'), or, from
 * an older builder, the builder type ('appointmentcompleted'). Null when unknown.
 */
export function resolveTrigger(value) {
  return triggerByBlockType(value) || triggerByType(value);
}

/** Same for an action ('SMS' / 'ASSIGN_TASK', or the builder type 'sms' / 'assigntask'). */
export function resolveAction(value) {
  return actionByBlockType(value) || actionByType(value);
}

/** Display label for a saved trigger; the raw value when unknown ('' for null / undefined). */
export function triggerLabel(value) {
  const entry = resolveTrigger(value);
  if (entry) return entry.label;
  return value === null || value === undefined ? '' : String(value);
}

/** Display label for a saved action; the raw value when unknown ('' for null / undefined). */
export function actionLabel(value) {
  const entry = resolveAction(value);
  if (entry) return entry.label;
  return value === null || value === undefined ? '' : String(value);
}

/** Icon key for a saved trigger; DEFAULT_TRIGGER_ICON when unknown. */
export function triggerIcon(value) {
  const entry = resolveTrigger(value);
  return entry ? entry.icon : DEFAULT_TRIGGER_ICON;
}

/** Icon key for a saved action; DEFAULT_ACTION_ICON when unknown. */
export function actionIcon(value) {
  const entry = resolveAction(value);
  return entry ? entry.icon : DEFAULT_ACTION_ICON;
}
